<template>
    <div class="phone-dialer-shell" @click.stop>
        <div id="phone-dialer">
            <!-- Active Calls List (shown when there are multiple calls) -->
            <div v-if="activeCalls.length > 1" class="active-calls-list">
                <div class="active-calls-header">Active Calls ({{ activeCalls.length }})</div>
                <div
                    v-for="callInfo in activeCalls"
                    :key="callInfo.sessionId"
                    class="call-item"
                    :class="{ active: callInfo.sessionId === currentSessionId, locked: hasPendingTransfer }"
                    :aria-disabled="hasPendingTransfer || undefined"
                    v-tooltip="hasPendingTransfer && PENDING_TRANSFER_TIP"
                    @click="switchToCall(callInfo.sessionId)"
                >
                    <div class="call-item-icon">
                        <i class="phone-icon" :class="callInfo.call.isIncoming ? 'fa fa-phone-arrow-down-left' : 'fa fa-phone-arrow-up-right'"></i>
                    </div>
                    <div class="call-item-info">
                        <span class="call-number">{{ callInfo.call.status === 'drafting' ? 'New Call' : callInfo.call.phoneNumber }}</span>
                        <span v-if="callInfo.call.status !== 'drafting'" class="call-status-badge">
                            <!-- The parked origin of a transfer is not a normal swappable call — say so
                                 in words rather than letting it read as an ordinary "On Hold" leg. -->
                            <template v-if="callInfo.call.attendedTransfer?.role === 'origin'">Pending transfer — caller on hold</template>
                            <template v-else-if="callInfo.call.mediaTransition === 'holding'">Holding...</template>
                            <template v-else-if="callInfo.call.mediaTransition === 'resuming'">Resuming...</template>
                            <template v-else-if="callInfo.call.isHeld">On Hold</template>
                            <template v-else-if="callInfo.call.status === 'connected'">{{ formatCallDuration(callInfo.call.duration) }}</template>
                            <template v-else>{{ getStatusText(callInfo.call) }}</template>
                        </span>
                    </div>
                    <button
                        v-if="callInfo.call.status !== 'drafting'"
                        class="call-item-hangup"
                        :disabled="!!callInfo.call.attendedTransfer"
                        :aria-label="callInfo.call.attendedTransfer ? PENDING_TRANSFER_TIP : 'Hang up this call'"
                        @click.stop="hangupCall(callInfo.sessionId)"
                        v-tooltip="callInfo.call.attendedTransfer ? PENDING_TRANSFER_TIP : 'Hang up this call'"
                    >
                        <i class="fa fa-phone-hangup"></i>
                    </button>
                </div>
            </div>

            <div class="call-src-wrapper">
                <Switcher v-model="callSource" :options="callSourceOptions" dark class="call-src" wide :border="true" :disabled="isInCall" />
                <button v-if="canClickToCall" v-tooltip="clickToCallTooltip" @click="setupClickToCall" class="side-button click-to-call-button">
                    <i class="fa fa-arrow-pointer" />
                </button>
            </div>

            <VfSmartSelect
                v-model="selectedExtension"
                class="extension-selector"
                :options="sortedExtensions"
                label="Call From"
                placeholder="Select extension"
                :formatter="formatNumber"
                :disabled="isInCall"
                v-tooltip="!isInCall && 'The extension to place the call from'"
            >
                <template #option="{ option }">
                    <div class="flex items-center gap-1">
                        {{ option.title }}
                        <i v-if="option.ref && subscribedExtensionIds.includes(option.ref.id)" class="fa-solid fa-star text-amber-400" />
                        <i
                            v-if="option.ref && option.ref.id === workstationExtensionId"
                            class="fa-regular fa-desktop text-ink-4"
                            v-tooltip="'This workstation’s phone'"
                        />
                    </div>
                </template>
            </VfSmartSelect>
            <input
                v-model="phoneNumber"
                type="text"
                placeholder="Enter phone number"
                class="phone-input"
                @keydown.enter="handleCallAction"
                :disabled="isInCall"
                v-autofocus
                ref="phoneNumberEl"
            />
            <div v-if="!isInCall && dialedPartyLabel" class="dialed-party">{{ dialedPartyLabel }}</div>
            <div v-if="isInCall" class="call-timer">
                {{ callStatus === 'connected' ? formattedCallDuration : callStatusText }}
            </div>

            <div v-if="transferReasonLabel || callContextEntries.length" class="call-context" aria-label="Caller details">
                <div v-if="transferReasonLabel" class="call-context__reason">{{ transferReasonLabel }}</div>
                <div v-for="entry in visibleCallContextEntries" :key="entry.label" class="call-context__row">
                    <span class="call-context__label">{{ entry.label }}</span>
                    <span class="call-context__value" :title="entry.value">{{ entry.value }}</span>
                </div>
                <button
                    v-if="callContextEntries.length > 3"
                    class="call-context__toggle"
                    type="button"
                    :aria-expanded="showAllCallDetails"
                    @click="showAllCallDetails = !showAllCallDetails"
                >
                    {{ showAllCallDetails ? 'Show fewer details' : `Show ${callContextEntries.length - 3} more details` }}
                </button>
            </div>

            <!-- Pending-transfer workspace. Deliberately outside the `showInCallActions`
                 branch: while the consultation is still ringing the dialer falls back to the
                 keypad, and the transfer state must stay visible the whole time. Everything
                 here is derived from the manager's `attendedTransfer` — there is no local
                 timer or cancel fallback, because a completion can be committed remotely even
                 when the response is ambiguous. -->
            <div v-if="attendedTransfer" class="pending-transfer" data-test="pending-transfer-banner" role="status">
                <template v-if="transferPhase === 'consulting'">
                    <div class="pending-transfer__title">Pending transfer</div>
                    <div class="pending-transfer__line">Caller on hold</div>
                    <div class="pending-transfer__line">
                        {{ consultationConnected ? `Consulting with ${transferTarget}` : `Waiting for ${transferTarget} to answer` }}
                    </div>
                </template>
                <div v-else-if="transferPhase === 'completing'" class="pending-transfer__title">Completing transfer…</div>
                <div v-else class="pending-transfer__title">Returning to caller…</div>

                <div v-if="isConsultationLeg" class="pending-transfer__actions">
                    <button
                        class="pending-transfer__btn complete"
                        data-test="complete-transfer-button"
                        :disabled="!canCompleteTransfer"
                        :aria-label="completeTransferHint"
                        v-tooltip="completeTransferHint"
                        @click="completeTransfer"
                    >
                        Complete Transfer
                    </button>
                    <button
                        class="pending-transfer__btn return"
                        data-test="return-to-caller-button"
                        :disabled="transferLocked"
                        :aria-label="transferLocked ? PENDING_TRANSFER_TIP : 'Return to caller'"
                        v-tooltip="transferLocked ? PENDING_TRANSFER_TIP : 'End the consultation and return to the caller'"
                        @click="returnToCaller"
                    >
                        Return to Caller
                    </button>
                </div>

                <p v-if="transferError" class="pending-transfer__error" role="alert">{{ transferError }}</p>
            </div>

            <!-- Show action buttons or DTMF keypad when connected, keypad otherwise. A live-listen
                 leg is receive-only, so it gets neither. -->
            <template v-if="isListenOnly" />
            <template v-else-if="showInCallActions">
                <div v-if="showDtmfKeypad" class="dtmf-container">
                    <Keypad class="mt-4" @press="handleKeypadPress" />
                    <button class="back-button" @click="showDtmfKeypad = false">
                        <i class="fa fa-arrow-left"></i>
                        <span>Back</span>
                    </button>
                </div>
                <div v-else class="call-actions">
                    <button class="action-button" :class="{ muted: isMuted }" @click="toggleMute">
                        <i :class="isMuted ? 'fa fa-microphone-slash' : 'fa fa-microphone'"></i>
                        <span>{{ isMuted ? 'Unmute' : 'Mute' }}</span>
                    </button>
                    <button
                        class="action-button"
                        :class="{ held: isHeld }"
                        @click="toggleHold"
                        :disabled="!!mediaTransition || hasPendingTransfer"
                        :aria-label="hasPendingTransfer ? PENDING_TRANSFER_TIP : holdButtonLabel"
                        v-tooltip="hasPendingTransfer && PENDING_TRANSFER_TIP"
                    >
                        <i :class="holdButtonIcon"></i>
                        <span>{{ holdButtonLabel }}</span>
                    </button>
                    <button
                        class="action-button"
                        @click="openTransfer"
                        :disabled="!!mediaTransition || hasPendingTransfer"
                        :aria-label="hasPendingTransfer ? PENDING_TRANSFER_TIP : 'Transfer'"
                        v-tooltip="hasPendingTransfer && PENDING_TRANSFER_TIP"
                    >
                        <i class="fa fa-exchange"></i>
                        <span>Transfer</span>
                    </button>
                    <button
                        class="action-button"
                        @click="showDtmfKeypad = true"
                        :disabled="isHeld || !!mediaTransition || (hasPendingTransfer && transferPhase !== 'consulting')"
                    >
                        <i class="fa fa-grid-2"></i>
                        <span>Keypad</span>
                    </button>
                </div>
                <!-- New Call button (only when current call is on hold) -->
                <button
                    v-if="isHeld && !mediaTransition"
                    class="new-call-button"
                    :disabled="hasPendingTransfer"
                    :aria-label="hasPendingTransfer ? PENDING_TRANSFER_TIP : 'New Call'"
                    v-tooltip="hasPendingTransfer && PENDING_TRANSFER_TIP"
                    @click="startNewCall()"
                >
                    <i class="fa fa-phone-plus"></i>
                    <span>New Call</span>
                </button>
            </template>
            <Keypad v-else class="mt-4" @press="handleKeypadPress" />

            <div class="bottom-row">
                <button v-if="deskPhoneCallState === 'placing'" class="call-button" disabled>Placing Call...</button>
                <button v-else-if="deskPhoneCallState === 'placed'" class="call-button placed" disabled>Call Placed</button>
                <button
                    v-else-if="callStatus === 'idle' || callStatus === 'drafting'"
                    class="call-button unplaced"
                    @click="handleCallAction"
                    :disabled="phoneNumber.length === 0"
                >
                    <span>Place Call</span>
                    <span>via {{ callSource === 'computer' ? 'Computer' : 'Desk Phone' }}</span>
                </button>
                <!-- Never hang a consulting attended call up generically — the primary red
                     action becomes the manager's return-to-caller path instead. -->
                <button
                    v-else-if="isConsultingConsultationLeg"
                    class="call-button hangup"
                    data-test="primary-return-to-caller"
                    @click="returnToCaller"
                >
                    Return to Caller
                </button>
                <button
                    v-else
                    class="call-button hangup"
                    :disabled="transferLocked || hasPendingTransfer"
                    v-tooltip="(transferLocked || hasPendingTransfer) && PENDING_TRANSFER_TIP"
                    @click="hangup()"
                >
                    Hang Up
                </button>

                <button v-if="callSource === 'computer'" class="side-button" v-tooltip="'Speaker/Microphone Settings'" @click="openAudioSettings">
                    <i class="fa fa-microphone-signal-meter" />
                </button>
                <button
                    class="side-button"
                    data-test="dialer-my-numbers"
                    aria-label="My Numbers & Notifications"
                    v-tooltip="'My Numbers & Notifications'"
                    @click="openMyNumbers"
                >
                    <i class="fa-regular fa-gear" />
                </button>
            </div>
        </div>

        <div class="directory-pane">
            <Switcher v-model="directoryMode" :options="{ extensions: 'Extensions', contacts: 'Contacts' }" dark wide :border="true" class="mb-2" />
            <ExtensionDirectory
                v-if="directoryMode === 'extensions'"
                class="directory-body"
                :selected-from-extension-id="selectedExtension?.id ?? null"
                @dial="onDirectoryDial"
                @pickup="onDirectoryPickup"
            />
            <FContactPhoneSearch v-else class="directory-body" dark :autofocus="false" @select="onContactSelected" />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { dataFrom } from '@zyno-io/openapi-client-codegen';
import { handleError, handleErrorAndAlert, presentOverlay, showToast, VfSmartSelect } from '@zyno-io/vue-foundation';
import { compact } from 'lodash';
import { computed, nextTick, onMounted, onUnmounted, ref, watch, type Ref } from 'vue';

import type { DialerAttachmentRequest, DialerListenRequest } from '@/shared/services/dialer.service';

import { GlobalState } from '@/global';
import { getContactMatchLabel } from '@/modules/crm/exports/contact-resolution';
import FContactPhoneSearch, { type IContactPhoneSelection } from '@/modules/crm/exports/f-contact-phone-search.vue';
import { getCallContextEntries, getTransferReasonLabel } from '@/modules/talk/shared/call-context';
import MSubscriptions from '@/modules/talk/shared/modals/m-subscriptions.vue';
import { partyLabelForNumber, resolvePartyNumbers } from '@/modules/talk/shared/party-names';
import { softphoneMgr, type CallState } from '@/modules/talk/shared/softphone.manager';
import { useTalkHelpers, useTalkSoftphoneHelpers, useTalkStore, useTalkWorkstationExtension } from '@/modules/talk/shared/store';
import { SuiteCrmContactsApi } from '@/openapi-clients-generated/suite';
import { TalkSoftphoneApi, type IExtensionResponse } from '@/openapi-clients-generated/talk';
import Switcher from '@/shared/components/switcher.vue';
import { DataCache } from '@/shared/data/cache';
import { usePersistedPreference } from '@/shared/helpers/prefs.helper';
import { ClickToCallExtension } from '@/shared/services/extension-click2call';

import ExtensionDirectory from './f-extension-directory.vue';
import Keypad from './keypad.vue';
import AudioSelectionModal from './m-audio-selection.vue';
import TransferCallModal from './m-transfer-call.vue';

const props = defineProps<{
    dialedNumberRef: Ref<string | null>;
    attachmentRequestRef: Ref<DialerAttachmentRequest | null>;
    listenRequestRef: Ref<DialerListenRequest | null>;
}>();

const talkStore = useTalkStore();
const { canUseSoftphone } = useTalkSoftphoneHelpers();
const { hasNumberPermission, formatNumber } = useTalkHelpers();
const { blocksPersonalFallback, workstationExtensionId } = useTalkWorkstationExtension();

const extensions = DataCache.getRef('talkExtensions');
const extensionsById = DataCache.getMapById('talkExtensions');
const subscribedNumbers = DataCache.getRef('talkSubscribedNumbers');

const subscribedExtensionIds = computed(() => subscribedNumbers.value?.filter(n => n.primary || n.starred).map(s => s.objectId) ?? []);

function isSoftphoneEligible(ext: IExtensionResponse): boolean {
    return !!(ext.enableSip && ext.enableSoftphone && hasNumberPermission(ext, 'softphone'));
}

// On a shared workstation (or a workstation session whose link hasn't resolved yet — fail CLOSED, see
// docs/talk-workstation-extension-plan.md finding #3) the linked extension itself, regardless of call
// source; `workstationExtensionId` already reads null while pending or on a dedicated workstation.
const workstationLinkedExtension = computed(() => {
    if (!blocksPersonalFallback.value || !extensions.value) return undefined;
    return extensions.value.find(ext => ext.id === workstationExtensionId.value);
});

// On a shared workstation "Computer" only exists at all when the linked extension is softphone-
// eligible for THIS user — the workstation link can only ever grant desk-phone access, never upgrade
// or downgrade the user's own softphone grant (docs/talk-workstation-extension-plan.md §0a). Elsewhere
// (dedicated workstation, browser) this is unchanged: any softphone-eligible extension makes it available.
const canUseComputerCallSource = computed(() => {
    if (blocksPersonalFallback.value) {
        const linked = workstationLinkedExtension.value;
        return !!linked && isSoftphoneEligible(linked);
    }
    return canUseSoftphone.value;
});

const callSourceOptions = computed(() => compact([canUseComputerCallSource.value && 'computer', 'deskPhone']));
const persistedCallSource = usePersistedPreference<'computer' | 'deskPhone'>('talk', `softphoneCallSource`, 'local', true, 'computer');
const callSource = computed({
    get() {
        return persistedCallSource.value === 'computer' && canUseComputerCallSource.value ? 'computer' : 'deskPhone';
    },

    set(value: 'computer' | 'deskPhone') {
        if (value === 'computer' && canUseComputerCallSource.value) {
            persistedCallSource.value = 'computer';
        } else {
            persistedCallSource.value = 'deskPhone';
        }
    }
});

// On a shared workstation the phone surfaces use ONLY the workstation's linked extension — the user's
// personal softphone/starred bindings are ignored, never OR'd in. See
// docs/talk-workstation-extension-plan.md §0a. `workstationExtensionId` already reads null on a
// dedicated workstation regardless of the server value, so that branch falls through unchanged below.
const eligibleExtensions = computed(() => {
    if (!extensions.value) return [];

    if (blocksPersonalFallback.value) {
        // `canUseComputerCallSource` above already proved eligibility for computer mode — never a
        // downgrade, never an upgrade — so the linked extension is the only candidate either way.
        const linked = workstationLinkedExtension.value;
        return linked ? [linked] : [];
    }

    if (callSource.value === 'deskPhone' || !canUseSoftphone.value) {
        return extensions.value.filter(ext => isSoftphoneEligible(ext) || (ext.enableSip && subscribedExtensionIds.value.includes(ext.id)));
    }
    return extensions.value.filter(isSoftphoneEligible);
});

const sortedExtensions = computed(() => {
    const myExtensions = eligibleExtensions.value.filter(ext => subscribedExtensionIds.value.includes(ext.id));
    const otherExtensions = eligibleExtensions.value.filter(ext => !subscribedExtensionIds.value.includes(ext.id));
    return [...myExtensions, ...otherExtensions];
});

const selectedExtension = ref<IExtensionResponse | null>(null);
const phoneNumber = ref('');
const isMuted = ref(false);
const isHeld = computed(() => currentCall.value?.isHeld ?? false);
// In-flight hold/resume media transition. During 'resuming', `isHeld` can flip
// false before WebRTC audio is actually connected, so surface the in-between
// state instead of showing the call as fully active too early.
const mediaTransition = computed(() => currentCall.value?.mediaTransition ?? null);
const holdButtonLabel = computed(() => {
    switch (mediaTransition.value) {
        case 'holding':
            return 'Holding...';
        case 'resuming':
            return 'Resuming...';
        default:
            return isHeld.value ? 'Resume' : 'Hold';
    }
});
const holdButtonIcon = computed(() => {
    if (mediaTransition.value === 'resuming' || isHeld.value) return 'fa fa-play';
    return 'fa fa-pause';
});
const showInCallActions = computed(() => callStatus.value === 'connected' || !!mediaTransition.value);
const showDtmfKeypad = ref(false);
const phoneNumberEl = ref<HTMLInputElement | null>(null);
const deskPhoneCallState = ref<'idle' | 'placing' | 'placed'>('idle');

// Persist the last used extension ID
const lastUsedExtensionId = usePersistedPreference<string | null>('talk', `lastSoftphoneExtensionId:${GlobalState.tenant.value?.id}`);

// Get the active session ID from the manager (reactive)
const activeSessionIdFromManager = softphoneMgr.getActiveSessionId();

// Use a local ref that syncs with the manager's active session
const currentSessionId = computed(() => activeSessionIdFromManager.value);

// Get all calls with session IDs for the call list
const allCallsWithSessionIds = softphoneMgr.getCallsWithSessionIds();

// Build a list of active calls for display (excluding idle and ended)
const activeCalls = computed(() => {
    return allCallsWithSessionIds.value.filter(({ call }) => call.status !== 'ended' && call.status !== 'idle');
});

// Get the current call from the store (if any)
const currentCall = computed(() => {
    if (!currentSessionId.value) return null;
    return softphoneMgr.getCall(currentSessionId.value).value;
});

// Derive local state from the store
const callStatus = computed(() => currentCall.value?.status ?? 'idle');
const callDuration = computed(() => currentCall.value?.duration ?? 0);

// isInCall is true when actively on a call (not idle, not drafting)
const isInCall = computed(() => callStatus.value !== 'idle' && callStatus.value !== 'drafting');
const isListenOnly = computed(() => !!currentCall.value?.isListenOnly);

const callStatusText = computed(() => getStatusText(currentCall.value));
const showAllCallDetails = ref(false);
const transferReasonLabel = computed(() => getTransferReasonLabel(currentCall.value?.transferReason));
const callContextEntries = computed(() => getCallContextEntries(currentCall.value?.popupInfo));
const visibleCallContextEntries = computed(() => (showAllCallDetails.value ? callContextEntries.value : callContextEntries.value.slice(0, 3)));

watch(currentSessionId, () => {
    showAllCallDetails.value = false;
});

// ── Attended transfer ────────────────────────────────────────────────────────
// Everything below is *derived* from the manager's `attendedTransfer`, which it
// stamps on both legs of the transaction. The UI never drives hold/place/end/
// resume itself; it only reflects the current phase and routes the three
// transaction-aware actions back to the manager.
const PENDING_TRANSFER_TIP = 'Unavailable during a pending transfer';

// Prefer the active call's copy, but fall back to whichever visible call carries
// it — during a return the manager may have already re-pointed the active session.
const attendedTransfer = computed(
    () => currentCall.value?.attendedTransfer ?? activeCalls.value.find(({ call }) => call.attendedTransfer)?.call.attendedTransfer ?? null
);
const transferPhase = computed(() => attendedTransfer.value?.phase ?? null);
const hasPendingTransfer = computed(() => !!attendedTransfer.value);
const transferTarget = computed(() => attendedTransfer.value?.destination ?? '');
const isConsultationLeg = computed(() => attendedTransfer.value?.consultationSessionId === currentSessionId.value);
const isConsultingConsultationLeg = computed(() => isConsultationLeg.value && transferPhase.value === 'consulting');
const consultationCall = computed(() => {
    const transfer = attendedTransfer.value;
    if (!transfer) return null;
    return activeCalls.value.find(({ sessionId }) => sessionId === transfer.consultationSessionId)?.call ?? null;
});
// Completion is gated on the consultation's authoritative normal status, not on
// any local optimism about the leg being "up".
const consultationConnected = computed(() => consultationCall.value?.status === 'connected');
// Once the completion is in flight there is no safe local undo — the bridge may
// commit it even if our response never arrives — so both phases lock the UI and
// stay locked until the manager clears the transaction or the calls end.
const transferLocked = computed(() => transferPhase.value === 'completing' || transferPhase.value === 'returning');
const canCompleteTransfer = computed(() => transferPhase.value === 'consulting' && consultationConnected.value && !mediaTransition.value);
const completeTransferHint = computed(() => {
    if (transferPhase.value === 'completing') return 'Completing transfer…';
    if (transferPhase.value === 'returning') return 'Returning to caller…';
    if (!consultationConnected.value) return `Waiting for ${transferTarget.value} to answer`;
    return `Complete the transfer to ${transferTarget.value}`;
});
const transferError = ref<string | null>(null);

watch(transferPhase, () => {
    transferError.value = null;
});

async function completeTransfer() {
    if (!currentSessionId.value || !canCompleteTransfer.value) return;
    transferError.value = null;
    const accepted = await softphoneMgr.completeAttendedTransfer(currentSessionId.value);
    // Deliberately no success toast: acceptance is not proof of completion. The
    // authoritative signal is the calls ending / re-homing, which this view
    // already reflects.
    if (!accepted && softphoneMgr.getAttendedTransfer(currentSessionId.value)?.phase === 'consulting') {
        transferError.value = 'The transfer could not be completed.';
    }
}

async function returnToCaller() {
    const sessionId = currentSessionId.value ?? attendedTransfer.value?.consultationSessionId;
    if (!sessionId || transferLocked.value) return;
    transferError.value = null;
    // The manager clears the transaction only once the held leg's resume/media
    // handshake confirms, so the "Returning…" state persists until then.
    await softphoneMgr.returnToHeldCaller(sessionId);
}

const canClickToCall = computed(() => {
    // Only show on Chrome and not in the desktop app
    if (GlobalState.isWorkstation || !navigator.userAgent.includes('Chrome')) {
        return false;
    }
    // Hide if extension is already installed and configured for this tenant
    if (ClickToCallExtension.matchesTenant.value) {
        return false;
    }
    return true;
});
const clickToCallTooltip = computed(() => {
    if (ClickToCallExtension.isWorkstationed()) {
        return `Set up click-to-call for ${GlobalState.tenant.value?.name}`;
    }
    return 'Install click-to-call extension';
});

function getStatusText(call: CallState | null): string {
    if (!call) return '?';
    switch (call.status) {
        case 'drafting':
            return 'Dialing...';
        case 'detecting':
            return 'Detecting network...';
        case 'connecting':
            return 'Connecting...';
        case 'waiting_audio':
            return 'Connecting audio...';
        case 'ringing':
            return call.isIncoming ? 'Answering...' : 'Ringing...';
        case 'connected':
            return 'Connected';
        case 'ended':
            return 'Call Ended';
        default:
            return '?';
    }
}

function formatCallDuration(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

const formattedCallDuration = computed(() => formatCallDuration(callDuration.value));

// ── Who's being dialed ───────────────────────────────────────────────────────
// Put a name to the digits as they're typed: an internal extension first, else a CRM contact.
const CONTACT_MATCH_DEBOUNCE_MS = 300;
// Below this many digits the phone search is too broad to mean anything (and short strings are
// usually extensions anyway).
const CONTACT_MATCH_MIN_DIGITS = 4;
// A complete NANP number, with or without its leading 1.
const FULL_NANP_PATTERN = /^1?\d{10}$/;

// `*`/`#` mean IVR/feature-code entry, never a phone number.
const dialedDigits = computed(() => (phoneNumber.value.includes('*') || phoneNumber.value.includes('#') ? '' : phoneNumber.value.replace(/\D/g, '')));
const dialedExtension = computed(() => (phoneNumber.value ? extensions.value?.find(ext => ext.extension === phoneNumber.value) : undefined));
// A complete number goes through the shared resolution cache; a partial one through the contact
// search below, which can match on a substring.
const dialedE164 = computed(() =>
    !dialedExtension.value && FULL_NANP_PATTERN.test(dialedDigits.value) ? `+1${dialedDigits.value.slice(-10)}` : undefined
);

const contactSearchLabel = ref<string>();
let contactSearchTimer: ReturnType<typeof setTimeout> | undefined;
let contactSearchSeq = 0;

watch([dialedDigits, dialedE164, dialedExtension], () => {
    const seq = ++contactSearchSeq;
    contactSearchLabel.value = undefined;
    clearTimeout(contactSearchTimer);
    if (dialedExtension.value || dialedDigits.value.length < CONTACT_MATCH_MIN_DIGITS) return;

    if (dialedE164.value) {
        // Anything already cached renders immediately (read below); this refreshes it in the
        // background, past the TTL, so a contact saved moments ago still shows up.
        const number = dialedE164.value;
        contactSearchTimer = setTimeout(() => resolvePartyNumbers([number], { force: true }), CONTACT_MATCH_DEBOUNCE_MS);
        return;
    }

    const digits = dialedDigits.value;
    contactSearchTimer = setTimeout(async () => {
        try {
            const results = dataFrom(await SuiteCrmContactsApi.getCrmContactsIndex({ query: { q: digits } }));
            if (contactSearchSeq !== seq) return;
            contactSearchLabel.value = getContactMatchLabel(results);
        } catch (err) {
            // A hint only — never interrupt dialing over it.
            handleError(err, 'phone-dialer:contact-match');
        }
    }, CONTACT_MATCH_DEBOUNCE_MS);
});

onUnmounted(() => clearTimeout(contactSearchTimer));

const dialedPartyLabel = computed(() => {
    if (dialedExtension.value) return dialedExtension.value.displayName || dialedExtension.value.name;
    if (dialedE164.value) return partyLabelForNumber(dialedE164.value);
    return contactSearchLabel.value;
});

const directoryMode = ref<'extensions' | 'contacts'>('extensions');

watch(
    [sortedExtensions, () => talkStore.selectedNumbers],
    () => {
        // A prior selection that's no longer valid for the current call source / eligible set (e.g.
        // the call source just flipped, or a shared workstation's linked extension changed) must not
        // linger — never let a call be placed from an extension the current mode no longer allows.
        if (selectedExtension.value && !sortedExtensions.value.some(ext => ext.id === selectedExtension.value?.id)) {
            selectedExtension.value = null;
        }

        if (!sortedExtensions.value.length) return;

        // Priority 1: Currently selected number from store
        const currentExtension = talkStore.selectedNumbers?.[0];
        if (currentExtension && 'extension' in currentExtension) {
            const matchingExtension = sortedExtensions.value.find(ext => ext.id === currentExtension.id);
            if (matchingExtension) {
                selectedExtension.value = matchingExtension;
                return;
            }
        }

        // Priority 2: Last used extension (if it exists in the list)
        if (lastUsedExtensionId.value) {
            const lastUsedExtension = sortedExtensions.value.find(ext => ext.id === lastUsedExtensionId.value);
            if (lastUsedExtension) {
                selectedExtension.value = lastUsedExtension;
                return;
            }
        }

        // Priority 3: First sorted extension
        if (!selectedExtension.value && sortedExtensions.value.length > 0) {
            selectedExtension.value = sortedExtensions.value[0] ?? null;
        }
    },
    { immediate: true }
);

// Sync state when the active call changes (but not for drafts - we set those)
watch(currentSessionId, newSessionId => {
    if (newSessionId) {
        const call = softphoneMgr.getCall(newSessionId).value;
        if (call && call.status !== 'drafting') {
            phoneNumber.value = call.phoneNumber;
            isMuted.value = call.rtcSession?.isMuted() ?? false;
            // Active calls are always via computer/softphone
            callSource.value = 'computer';
        }
    }
});

// Sync phone number changes to draft call
watch(phoneNumber, newNumber => {
    if (currentSessionId.value && callStatus.value === 'drafting') {
        softphoneMgr.updateDraftCallNumber(currentSessionId.value, newNumber);
    }
});

// When the component mounts, check if there's an active call and restore it
onMounted(() => {
    if (currentSessionId.value) {
        // Restore the phone number from the active call
        const call = softphoneMgr.getCall(currentSessionId.value).value;
        if (call) {
            phoneNumber.value = call.phoneNumber;
            // Sync mute state
            isMuted.value = call.rtcSession?.isMuted() ?? false;
        }
    }
});

// Watch for dialed number changes from external sources (click-to-call, etc.)
watch(
    () => props.dialedNumberRef.value,
    newNumber => {
        if (newNumber) {
            startNewCall(newNumber);
            props.dialedNumberRef.value = null;
        }
    },
    { immediate: true }
);

// Watch for a directed pickup / exact parking retrieval handed off from elsewhere in the app (the
// directory pane's own pickup button goes through `onDirectoryPickup` instead, without a round trip
// through the dock).
watch(
    () => props.attachmentRequestRef.value,
    request => {
        if (request) {
            runAttachment(request);
            props.attachmentRequestRef.value = null;
        }
    },
    { immediate: true }
);

watch(
    () => props.listenRequestRef.value,
    request => {
        if (request) {
            void runListen(request);
            props.listenRequestRef.value = null;
        }
    },
    { immediate: true }
);

async function handleCallAction() {
    if (callSource.value === 'deskPhone') {
        await placeDeskPhoneCall();
    } else if (callStatus.value === 'drafting') {
        // Place the draft call
        await placeDraftCall();
    } else if (callStatus.value === 'idle') {
        // Place a new call directly
        placeCall();
    }
}

function placeCall() {
    if (!phoneNumber.value || !selectedExtension.value) return;
    // Structural invariant from callSource/eligibleExtensions above should already guarantee this —
    // never place a computer (softphone) call from an extension this session can't legally use one
    // from, e.g. a desk-only linked extension on a shared workstation.
    if (blocksPersonalFallback.value && !isSoftphoneEligible(selectedExtension.value)) return;

    // Save the last used extension ID
    lastUsedExtensionId.value = selectedExtension.value.id;

    // Place call via the manager
    softphoneMgr.placeCall(selectedExtension.value.id, phoneNumber.value);
}

// Contact pick from the directory pane: load the number into the field rather than dialing it, so
// the user still confirms — a contact row is a search result, not a dial button like an extension.
function onContactSelected(selection: IContactPhoneSelection) {
    if (isInCall.value) {
        startNewCall(selection.phone);
        return;
    }
    phoneNumber.value = selection.phone;
    nextTick(() => phoneNumberEl.value?.focus());
}

// Click-to-call from the directory pane: dial the chosen extension from the selected "Call From".
function onDirectoryDial(ext: IExtensionResponse) {
    if (!selectedExtension.value) return;
    // Don't disrupt a live call — open a prefilled draft the user can confirm instead.
    if (isInCall.value) {
        startNewCall(ext.extension);
        return;
    }
    phoneNumber.value = ext.extension;
    void handleCallAction();
}

// Directed pickup from the directory pane. When the presence feed knows the ringing call's ID and
// we're calling from the computer, run the exact attachment RPC (`**<ext>` above becomes just the
// label shown while it connects); otherwise fall back to dialing `**<ext>` as before, which also
// covers desk-phone click-to-call. No eligibility check here either way — the target's pickup policy
// is the backend's, and a refusal ("Pickup not allowed", "No ringing call to pick up") arrives
// through the same FAILED alert / UserError as any other call.
function onDirectoryPickup(ext: IExtensionResponse, callId: string | null) {
    if (!selectedExtension.value) return;
    if (callSource.value === 'computer' && callId) {
        runAttachment({ kind: 'pickup', callId, targetExtensionId: ext.id, label: `**${ext.extension}` });
        return;
    }
    const destination = `**${ext.extension}`;
    if (isInCall.value) {
        startNewCall(destination);
        return;
    }
    phoneNumber.value = destination;
    void handleCallAction();
}

// Runs a directed pickup / exact parking retrieval from the selected "Call From" extension —
// entered either via `onDirectoryPickup` above or handed off through `attachmentRequestRef` (the
// dock's `registerAttachmentHandler`, e.g. from the parking screen's Retrieve button).
function runAttachment(request: DialerAttachmentRequest) {
    if (!selectedExtension.value) {
        showToast({ message: 'Choose a "Call From" extension first', durationSecs: 4 });
        return;
    }
    // Mid-call there is no draft-style "queue this and hold the current call" step for an
    // attachment (unlike `placeCall`, which the dialer only ever fires while idle) — require the
    // user to finish or hold first rather than silently holding a call out from under them.
    if (isInCall.value) {
        showToast({ message: 'Finish or hold the current call first', durationSecs: 4 });
        return;
    }
    if (callSource.value === 'deskPhone') {
        // A desk phone can't answer a WebRTC offer, so dial the equivalent shortcut instead.
        const targetExtensionNumber = request.kind === 'pickup' ? extensionsById.value[request.targetExtensionId]?.extension : undefined;
        phoneNumber.value = request.kind === 'pickup' ? (targetExtensionNumber ? `**${targetExtensionNumber}` : request.label) : request.label;
        void placeDeskPhoneCall();
        return;
    }
    // A draft the user was typing into would otherwise linger beside the new session.
    if (currentSessionId.value && callStatus.value === 'drafting') softphoneMgr.cancelDraftCall(currentSessionId.value);
    lastUsedExtensionId.value = selectedExtension.value.id;
    softphoneMgr.placeAttachment(selectedExtension.value.id, request);
    phoneNumber.value = request.label;
}

async function runListen(request: DialerListenRequest) {
    if (callSource.value !== 'computer') {
        callSource.value = 'computer';
        await nextTick();
    }
    if (selectedExtension.value && !eligibleExtensions.value.some(extension => extension.id === selectedExtension.value?.id)) {
        selectedExtension.value = sortedExtensions.value[0] ?? null;
    }
    if (!selectedExtension.value) {
        showToast({ message: 'Choose a "Call From" extension first', durationSecs: 4 });
        return;
    }
    if (isInCall.value) {
        showToast({ message: 'Finish the current call before listening to another call', durationSecs: 4 });
        return;
    }
    if (currentSessionId.value && callStatus.value === 'drafting') softphoneMgr.cancelDraftCall(currentSessionId.value);
    lastUsedExtensionId.value = selectedExtension.value.id;
    softphoneMgr.placeListenCall(selectedExtension.value.id, request.callId, request.label);
    phoneNumber.value = request.label;
}

async function placeDraftCall() {
    if (!currentSessionId.value || !phoneNumber.value) return;

    // Save the last used extension ID
    if (selectedExtension.value) {
        lastUsedExtensionId.value = selectedExtension.value.id;
    }

    // Place the draft call
    await softphoneMgr.placeDraftCall(currentSessionId.value);
}

async function placeDeskPhoneCall() {
    if (!phoneNumber.value || !selectedExtension.value) return;

    // Save the last used extension ID
    lastUsedExtensionId.value = selectedExtension.value.id;

    // Show "Placing Call..." state
    deskPhoneCallState.value = 'placing';

    try {
        // Place call via desk phone API
        await TalkSoftphoneApi.postSoftphoneCallViaDeskPhone({
            body: {
                extensionId: selectedExtension.value.id,
                destinationNumber: phoneNumber.value
            }
        });

        // Show "Call Placed" state
        deskPhoneCallState.value = 'placed';

        // After 1 second, revert and clear
        setTimeout(() => {
            deskPhoneCallState.value = 'idle';
            phoneNumber.value = '';
        }, 2_000);
    } catch (err) {
        deskPhoneCallState.value = 'idle';
        handleErrorAndAlert(err);
    }
}

function startNewCall(initialNumber?: string) {
    if (mediaTransition.value) return;
    // A new call would hold/park legs the transfer transaction owns.
    if (hasPendingTransfer.value) return;
    // If no active session or current call is already a draft, just update the number
    if (!currentSessionId.value || callStatus.value === 'drafting') {
        phoneNumber.value = initialNumber ?? '';
        nextTick(() => phoneNumberEl.value?.focus());
        return;
    }

    if (!selectedExtension.value) return;
    softphoneMgr.createDraftCall(selectedExtension.value.id);
    phoneNumber.value = initialNumber ?? '';
    nextTick(() => phoneNumberEl.value?.focus());
}

async function hangup() {
    if (!currentSessionId.value) return;
    // The transaction may have placed this leg on hold; a generic end would
    // race the attended-transfer state machine. The consulting consultation is
    // exited only through Return to caller above.
    if (softphoneMgr.getCall(currentSessionId.value).value?.attendedTransfer) return;
    await softphoneMgr.hangup(currentSessionId.value);

    // Clear the phone number after hanging up
    phoneNumber.value = '';
}

async function hangupCall(sessionId: string) {
    // Either leg of a transfer transaction is off-limits to the generic hangup;
    // use Return to Caller instead.
    if (softphoneMgr.getCall(sessionId).value?.attendedTransfer) return;
    await softphoneMgr.hangup(sessionId);
}

async function switchToCall(sessionId: string) {
    if (sessionId === currentSessionId.value) return;
    // Switching resumes the other leg — which, mid-transfer, is the parked caller.
    if (hasPendingTransfer.value) return;
    await softphoneMgr.switchToCall(sessionId);
}

function handleKeypadPress(digit: string) {
    if (callStatus.value === 'connected' && currentSessionId.value) {
        // Send DTMF tone over the RTC connection
        softphoneMgr.sendDTMF(currentSessionId.value, digit);
    } else if (callStatus.value === 'idle' || callStatus.value === 'drafting') {
        // Add to phone number input
        phoneNumber.value += digit;
    }
}

function toggleMute() {
    if (!currentSessionId.value) return;
    softphoneMgr.toggleMute(currentSessionId.value);
    isMuted.value = !isMuted.value;
}

async function toggleHold() {
    if (!currentSessionId.value) return;
    // Ignore taps while a hold/resume is already in flight — the manager owns the sequencing.
    if (mediaTransition.value) return;
    // The transfer transaction owns the hold state of both its legs.
    if (hasPendingTransfer.value) return;
    await softphoneMgr.toggleHold(currentSessionId.value);
}

async function openTransfer() {
    if (!selectedExtension.value || !currentSessionId.value) return;
    if (hasPendingTransfer.value) return;

    await presentOverlay(TransferCallModal, {
        currentExtensionId: selectedExtension.value.id,
        sessionId: currentSessionId.value
    });
}

async function openAudioSettings() {
    await presentOverlay(AudioSelectionModal, {});
}

// The same modal as Talk's header gear — which numbers are starred, and where they notify.
async function openMyNumbers() {
    await presentOverlay(MSubscriptions, {});
}

function setupClickToCall() {
    if (ClickToCallExtension.isWorkstationed()) {
        // Extension is installed but not configured for this tenant - configure it
        ClickToCallExtension.setPreferredTenant();
    } else {
        // Extension not installed - open install page
        window.open(ClickToCallExtension.getInstallUrl(), '_blank');
    }
}
</script>

<style scoped>
@reference '#tw.css';

/* Dark in both themes, on the `--chrome-panel-*` ladder (foundation.css). Text on it is
   `ink-on-chrome` / `-2` or white — the `ink-*` ramp flips with the theme and goes illegible here. */
.phone-dialer-shell {
    @apply mt-2 p-3 bg-chrome-panel border-chrome-panel rounded-md flex items-stretch;
}

#phone-dialer {
    @apply w-72 flex-shrink-0 pr-3;
}

/* Wrapper owns the pane's height so whichever directory is showing can scroll inside it. */
.directory-pane {
    @apply w-64 flex-shrink-0 border-l border-chrome-panel-raised pl-3 flex flex-col min-h-0;
}

.directory-body {
    @apply flex-1 min-h-0;
}

/* Who the typed digits belong to — an extension name, or a matched CRM contact. */
.dialed-party {
    @apply text-sm text-ink-on-chrome-2 text-center truncate mt-1;
}

.active-calls-list {
    @apply mb-3 border border-chrome-panel-raised rounded overflow-hidden;
}

.active-calls-header {
    @apply px-3 py-2 bg-chrome-panel-raised text-sm font-semibold text-ink-on-chrome-2;
}

.call-item {
    @apply flex items-center justify-between px-3 py-2 bg-chrome-panel-raised cursor-pointer border-t border-chrome-panel-raised;

    &:hover {
        @apply bg-chrome-panel-raised-2;
    }

    /* Inverted, so the current call stands out from the raised rows in either theme. */
    &.active {
        @apply bg-ink-on-chrome;

        i.phone-icon,
        .call-number,
        .call-status-badge {
            @apply text-chrome-panel;
        }
    }
}

.call-item-icon {
    @apply w-6 text-ink-on-chrome-2 flex-shrink-0;

    i {
        @apply text-sm;
    }
}

.call-item-info {
    @apply flex flex-col gap-0.5 min-w-0 flex-1;
}

.call-number {
    @apply text-white text-sm font-medium truncate;
}

.call-status-badge {
    @apply text-xs text-ink-on-chrome-2;
}

.call-item-hangup {
    @apply ml-2 w-8 h-8 flex items-center justify-center bg-err-fill hover:bg-err text-white rounded border-none cursor-pointer;

    i {
        @apply text-sm;
    }

    &:disabled {
        @apply opacity-50 cursor-not-allowed hover:bg-err-fill;
    }
}

/* Mid-transfer the list is read-only: switching would resume the parked caller. */
.call-item.locked {
    @apply cursor-not-allowed;

    &:hover {
        @apply bg-chrome-panel-raised;
    }

    &.active:hover {
        @apply bg-ink-on-chrome;
    }
}

.pending-transfer {
    @apply mt-3 p-3 rounded border border-warn-fill bg-chrome-panel-raised;
}

.pending-transfer__title {
    @apply text-sm font-semibold text-amber-300;
}

.pending-transfer__line {
    @apply text-xs text-ink-on-chrome mt-1;
}

.pending-transfer__actions {
    @apply flex gap-2 mt-3;
}

.pending-transfer__btn {
    @apply flex-1 h-auto p-2 text-sm font-medium text-white rounded border-none cursor-pointer;

    &.complete {
        @apply bg-ok-fill hover:bg-ok;
    }

    &.return {
        @apply bg-chrome-panel-raised-2 hover:bg-chrome-panel-raised-3;
    }

    &:disabled {
        @apply opacity-50 cursor-not-allowed;
    }
}

.pending-transfer__error {
    @apply text-xs text-red-300 mt-2;
}

.bottom-row {
    @apply flex gap-1 mt-4;
}

.side-button {
    @apply w-12 h-12 flex items-center justify-center bg-chrome-panel-raised-2 hover:bg-chrome-panel-raised-3 text-ink-on-chrome-2 hover:text-white rounded border-none cursor-pointer;
}

.call-src-wrapper {
    @apply flex items-center gap-1 mb-1;

    .call-src {
        @apply flex-1;
    }

    .click-to-call-button {
        @apply rounded-lg;
    }
}

/* The Computer/Desk Phone toggle is a primary control here, so override the
   default 30px control-toggle height for a more tappable, prominent size that
   also lines up with the adjacent square buttons. */
:deep(.call-src button) {
    height: 44px;
    font-size: var(--t-md);
}

.extension-selector {
    @apply mb-1;

    :deep(input) {
        @apply text-center text-lg bg-chrome-panel-raised text-white border-transparent focus:border-chrome-panel;

        &:disabled {
            @apply bg-chrome-panel-raised;
        }

        &::placeholder {
            @apply text-ink-on-chrome-2;
        }
    }

    &:after {
        @apply !border-ink-on-chrome-2;
    }
}

.phone-input {
    @apply w-full text-center text-xl p-2 border bg-chrome-panel-raised text-white border-transparent focus:border-chrome-panel rounded;

    &:disabled {
        @apply bg-chrome-panel-raised;
    }

    &::placeholder {
        @apply text-ink-on-chrome-2;
    }
}

.call-timer {
    @apply text-center text-xl font-semibold text-ink-on-chrome my-5;
}

.call-context {
    @apply mt-3 p-3 rounded border border-chrome-panel-raised-2 bg-chrome-panel-raised;
}

.call-context__reason {
    @apply text-xs font-semibold text-ink-on-chrome mb-2;
}

.call-context__row {
    @apply flex gap-3 text-xs mt-1;
}

/* `ink-*` is ink on `--bg`; this panel is chrome, so the secondary tone has to be the on-chrome one.
   `text-ink-4` reads fine in light mode by luck and drops to ~2.4:1 on the dark chrome in dark mode. */
.call-context__label {
    @apply text-ink-on-chrome-2 shrink-0;
}

.call-context__value {
    @apply text-ink-on-chrome text-right truncate flex-1;
}

/* Not `text-accent`: the accent flips with the theme, not with the surface, and `--accent` is a dark
   blue in light mode — near-illegible on this panel. On-chrome ink is the only tone that holds in
   both, so the underline carries the link affordance the colour can't. */
.call-context__toggle {
    @apply h-auto mt-2 p-0 bg-transparent hover:bg-transparent border-none text-xs font-semibold text-ink-on-chrome-2 hover:text-ink-on-chrome hover:underline cursor-pointer;
}

.call-actions {
    @apply grid grid-cols-2 gap-2 mt-4;
}

.new-call-button {
    @apply w-full flex items-center justify-center h-auto gap-2 mt-2 p-3 bg-ok-fill hover:bg-ok-fill text-white rounded border-none cursor-pointer;

    i {
        @apply text-base;
    }

    span {
        @apply text-sm font-medium;
    }
}

.dtmf-container {
    @apply mt-4;
}

.back-button {
    @apply w-full flex items-center justify-center h-auto gap-2 mt-3 p-3 bg-chrome-panel-raised-2 hover:bg-chrome-panel-raised-3 text-white rounded border-none cursor-pointer;
}

.action-button {
    /* h-auto escapes the global `button { height: var(--ctrl-h) }` reset, which
       would otherwise clamp these stacked icon+label buttons to 30px and
       collapse the icon onto the label. */
    @apply flex flex-col items-center justify-center h-auto p-4 bg-chrome-panel-raised-2 hover:bg-chrome-panel-raised-3 text-white rounded border-none cursor-pointer;

    i {
        @apply text-2xl mb-2;
    }

    span {
        @apply text-sm;
    }

    &.held {
        @apply bg-warn-fill hover:bg-warn-fill;
    }

    &.muted {
        @apply bg-warn-fill hover:bg-warn-fill;
    }

    &:disabled {
        @apply opacity-50 cursor-not-allowed;
    }
}

.call-button {
    @apply flex-1 h-auto p-3 bg-chrome-panel-raised-2 hover:bg-chrome-panel-raised-3 text-white font-semibold rounded border-none;

    &.hangup {
        @apply bg-err-fill hover:bg-err;
    }

    &.placed {
        @apply bg-ok-fill;
    }

    &:disabled {
        @apply opacity-70 cursor-not-allowed;
    }

    &.unplaced {
        @apply flex flex-col items-center justify-center p-1 h-12;

        span:last-child {
            @apply text-xs font-normal;
        }

        &:not([disabled]) {
            @apply bg-ok-fill hover:bg-ok-fill;
        }
    }
}

.switcher:deep(button) {
    @apply text-nowrap text-sm;
}
</style>
